import {
  AiAmrEnergyConsumption,
  AiAmrMeasuredValue,
  AiAmrOverviewMetric,
  AiAmrSummary,
  AiAmrTrainingZone,
} from "./aiAmrSummaryTypes";
import {
  parseAiSummaryNutrition,
  parseAiSummaryRecommendations,
  parseAiSummaryTrainingPlan,
} from "./aiSummaryResponse";

type UnknownRecord = Record<string, unknown>;

const MEASURED_VALUE_LABELS: Record<
  string,
  { label: string; defaultUnit?: string }
> = {
  VO2PEAK: { label: "VO2 Peak", defaultUnit: "ml/min/kg" },
  HRMAX: { label: "HR Max", defaultUnit: "bpm" },
  VENTILATORYTHRESHOLD: { label: "VT1", defaultUnit: "bpm" },
  ANAEROBICTHRESHOLD: { label: "VT2", defaultUnit: "bpm" },
  FATMAX: { label: "Fat-Max HR", defaultUnit: "bpm" },
  CROSSOVER: { label: "Cross-over HR", defaultUnit: "bpm" },
};

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getString = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const getCleanString = (value: unknown): string =>
  getString(value).replace(/^:\s*/, "");

const getFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim()) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : undefined;
  }

  return undefined;
};

const getPositiveNumber = (value: unknown): number | undefined => {
  const numericValue = getFiniteNumber(value);
  return numericValue !== undefined && numericValue > 0
    ? numericValue
    : undefined;
};

const parseJsonString = (value: string): unknown => {
  const normalized = value
    .trim()
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/, "");

  try {
    return JSON.parse(normalized);
  } catch (error) {
    return value;
  }
};

const isAmrPayload = (value: UnknownRecord): boolean =>
  "training_zones" in value ||
  "energy_consumption" in value ||
  "overview_metrics" in value ||
  "measured_values" in value ||
  "workout" in value;

const unwrapResponse = (payload: unknown): unknown => {
  let current =
    typeof payload === "string" ? parseJsonString(payload) : payload;

  for (let depth = 0; depth < 4; depth += 1) {
    if (!isRecord(current) || isAmrPayload(current)) {
      return current;
    }

    const nested = current.data ?? current.response ?? current.result;
    if (nested === undefined || nested === current) {
      return current;
    }

    current = typeof nested === "string" ? parseJsonString(nested) : nested;
  }

  return current;
};

const humanizeIdentifier = (value: string): string =>
  value
    .replace(/_AMR$/i, "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const parseOverviewMetrics = (value: unknown): AiAmrOverviewMetric[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<AiAmrOverviewMetric[]>((metrics, item) => {
    if (!isRecord(item)) {
      return metrics;
    }

    const metricValue = getFiniteNumber(item.value);
    const label = getString(item.label);
    const scoreCategory = getFiniteNumber(item.score_category);
    const scoreCategoryLabel = getString(item.score_category_label);
    const isUnavailablePlaceholder =
      metricValue === 0 &&
      (!scoreCategory || scoreCategory === 0) &&
      !scoreCategoryLabel;

    if (
      metricValue === undefined ||
      metricValue < 0 ||
      isUnavailablePlaceholder ||
      !label
    ) {
      return metrics;
    }

    metrics.push({
      id: getString(item.id) || label,
      label,
      value: Math.max(0, Math.min(100, metricValue)),
      unit: getString(item.unit),
      ...(scoreCategory !== undefined ? { scoreCategory } : {}),
      scoreCategoryLabel,
    });

    return metrics;
  }, []);
};

const parseMeasuredValues = (value: unknown): AiAmrMeasuredValue[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<AiAmrMeasuredValue[]>((measurements, item) => {
    if (!isRecord(item)) {
      return measurements;
    }

    const rawId = getString(item.id);
    const id = rawId.toUpperCase();
    const measurementValue = getPositiveNumber(item.value);
    if (!id || measurementValue === undefined) {
      return measurements;
    }

    const presentation = MEASURED_VALUE_LABELS[id];
    measurements.push({
      id,
      label: presentation?.label ?? humanizeIdentifier(rawId),
      value: measurementValue,
      unit: getString(item.unit) || presentation?.defaultUnit || "",
    });

    return measurements;
  }, []);
};

const parseTrainingZones = (value: unknown): AiAmrTrainingZone[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .reduce<AiAmrTrainingZone[]>((zones, item) => {
      if (!isRecord(item)) {
        return zones;
      }

      const zone = getPositiveNumber(item.zone);
      const wattsLower = getPositiveNumber(item.watts_lower);
      const wattsUpper = getPositiveNumber(item.watts_upper);
      const heartRateLowerBpm = getPositiveNumber(item.hr_lower_bpm);
      const heartRateUpperBpm = getPositiveNumber(item.hr_upper_bpm);
      if (
        zone === undefined ||
        (wattsLower === undefined &&
          wattsUpper === undefined &&
          heartRateLowerBpm === undefined &&
          heartRateUpperBpm === undefined)
      ) {
        return zones;
      }

      zones.push({
        zone,
        ...(wattsLower !== undefined ? { wattsLower } : {}),
        ...(wattsUpper !== undefined ? { wattsUpper } : {}),
        ...(heartRateLowerBpm !== undefined ? { heartRateLowerBpm } : {}),
        ...(heartRateUpperBpm !== undefined ? { heartRateUpperBpm } : {}),
      });
      return zones;
    }, [])
    .sort((left, right) => right.zone - left.zone);
};

const parseEnergyConsumption = (value: unknown): AiAmrEnergyConsumption[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .reduce<AiAmrEnergyConsumption[]>((zones, item) => {
      if (!isRecord(item)) {
        return zones;
      }

      const zone = getPositiveNumber(item.zone);
      const fatPercent = getFiniteNumber(item.fat_pct);
      const carbPercent = getFiniteNumber(item.carb_pct);
      const kcalPerMinute = getFiniteNumber(item.kcal_per_min);
      if (
        zone === undefined ||
        (fatPercent === undefined &&
          carbPercent === undefined &&
          kcalPerMinute === undefined)
      ) {
        return zones;
      }

      zones.push({
        zone,
        ...(fatPercent !== undefined
          ? { fatPercent: Math.max(0, Math.min(100, fatPercent)) }
          : {}),
        ...(carbPercent !== undefined
          ? { carbPercent: Math.max(0, Math.min(100, carbPercent)) }
          : {}),
        ...(kcalPerMinute !== undefined && kcalPerMinute >= 0
          ? { kcalPerMinute }
          : {}),
      });
      return zones;
    }, [])
    .sort((left, right) => right.zone - left.zone);
};

export const parseAiAmrSummaryResponse = (payload: unknown): AiAmrSummary => {
  const unwrapped = unwrapResponse(payload);
  const root = isRecord(unwrapped) ? unwrapped : {};
  const patient = isRecord(root.patient) ? root.patient : {};
  const services = isRecord(root.services) ? root.services : {};
  const recommendations = isRecord(root.recommendations)
    ? root.recommendations
    : {};
  const biologicalAge = getFiniteNumber(patient.biological_age);
  const chronologicalAge = getFiniteNumber(patient.chronological_age);
  const rootNutrition = parseAiSummaryNutrition(root.nutrition);
  const recommendationNutrition = parseAiSummaryNutrition(
    recommendations.nutrition
  );

  return {
    patient: {
      name: getCleanString(patient.name),
      testDate: getCleanString(patient.test_date),
      companyName: getCleanString(patient.company_name),
      ...(biologicalAge !== undefined ? { biologicalAge } : {}),
      ...(chronologicalAge !== undefined ? { chronologicalAge } : {}),
    },
    legend: getString(root.legend),
    reportType: getString(root.report_type),
    bioAgeDescription: getString(root.bio_age_description),
    vo2PeakDescription: getString(root.vo2_peak_description),
    overviewMetrics: parseOverviewMetrics(root.overview_metrics),
    measuredValues: parseMeasuredValues(root.measured_values),
    trainingZones: parseTrainingZones(root.training_zones),
    energyConsumption: parseEnergyConsumption(root.energy_consumption),
    trainingPlan: parseAiSummaryTrainingPlan(
      root.workout ?? recommendations.exercise
    ),
    nutrition: {
      workoutDays:
        rootNutrition.workoutDays ?? recommendationNutrition.workoutDays,
      nonWorkoutDays:
        rootNutrition.nonWorkoutDays ?? recommendationNutrition.nonWorkoutDays,
    },
    recommendations: parseAiSummaryRecommendations(recommendations, services),
    disclaimer: getString(root.disclaimer),
  };
};
